import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { fmtMoney } from "@/lib/format";
import { WidgetError } from "@/components/WidgetError";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Wallet, AlertTriangle, TrendingDown, Pencil } from "lucide-react";

/**
 * Customer Wallet card — read-only projection of backend ledger truth.
 *
 * Consumes: GET /finance/customers/{cid}/wallet
 * The backend computes balance, aging and available_facility from the
 * append-only ledger. This component NEVER re-computes those numbers.
 *
 * Owner/admin can edit the credit_limit inline; the backend re-computes and
 * returns the fresh wallet in the PUT response so we update in one round-trip.
 */
export default function CustomerWalletCard({ customerId }) {
  const [wallet, setWallet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(false);
  const { user } = useAuth();
  const canEditLimit = user && (user.role === "truckstop_owner" ||
                                user.role === "truckstop_admin");

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    api.get(`/finance/customers/${customerId}/wallet`)
      .then(({ data }) => setWallet(data))
      .catch(setError)
      .finally(() => setLoading(false));
  }, [customerId]);
  useEffect(load, [load]);

  if (error) {
    return <WidgetError error={error} onRetry={load}
                        title="Could not load wallet" testId="wallet-error"/>;
  }
  if (loading || !wallet) {
    return (
      <div className="bg-white border border-slate-200 p-5" data-testid="customer-wallet-loading">
        <div className="label-overline mb-3">Customer Wallet</div>
        <div className="text-xs text-slate-500">Loading…</div>
      </div>
    );
  }

  const { balance_cents, credit_limit_cents, available_facility_cents,
          overdue_cents, unallocated_credit_cents, aging } = wallet;

  const overFacility =
    credit_limit_cents !== null && credit_limit_cents !== undefined &&
    balance_cents > credit_limit_cents;

  return (
    <div className="bg-white border border-slate-200 p-5" data-testid="customer-wallet-card">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Wallet size={16} className="text-slate-700"/>
          <div className="label-overline">Customer Wallet</div>
        </div>
        {overFacility && (
          <span className="text-[10px] font-bold uppercase tracking-wider border border-red-300 bg-red-50 text-red-700 px-2 py-0.5 rounded"
                data-testid="wallet-over-facility-flag">
            <AlertTriangle size={10} className="inline mr-1"/>Over facility
          </span>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <Metric testId="wallet-balance" label="Outstanding"
                value={fmtMoney(Math.max(balance_cents, 0))}
                tone={balance_cents > 0 ? "warn" : "neutral"}/>
        <Metric testId="wallet-overdue" label="Overdue"
                value={fmtMoney(overdue_cents)}
                tone={overdue_cents > 0 ? "danger" : "neutral"}/>
        <Metric testId="wallet-credit-limit" label="Credit limit"
                value={credit_limit_cents == null ? "—" : fmtMoney(credit_limit_cents)}
                actionIcon={canEditLimit ? (
                  <button type="button"
                          data-testid="wallet-credit-limit-edit-btn"
                          onClick={() => setEditing(true)}
                          className="text-slate-400 hover:text-blue-700 transition"
                          aria-label="Edit credit limit">
                    <Pencil size={12}/>
                  </button>
                ) : null}/>
        <Metric testId="wallet-available-facility" label="Available facility"
                value={available_facility_cents == null ? "—" : fmtMoney(available_facility_cents)}
                tone={available_facility_cents != null && available_facility_cents <= 0 ? "danger" : "good"}/>
      </div>

      <div className="border-t border-slate-100 pt-4">
        <div className="label-overline text-slate-500 text-[10px] mb-2">Aging (past due days)</div>
        <div className="grid grid-cols-5 gap-2 text-center">
          <Bucket label="Current" value={aging.current} testId="wallet-aging-current"/>
          <Bucket label="1–30" value={aging.d30} testId="wallet-aging-d30"/>
          <Bucket label="31–60" value={aging.d60} testId="wallet-aging-d60"/>
          <Bucket label="61–90" value={aging.d90} testId="wallet-aging-d90"/>
          <Bucket label="90+" value={aging.d120plus} testId="wallet-aging-d120plus"
                  danger/>
        </div>
      </div>

      {unallocated_credit_cents > 0 && (
        <div className="mt-4 border border-blue-200 bg-blue-50 rounded p-3 text-xs text-blue-800 flex items-center gap-2"
             data-testid="wallet-unallocated-credit">
          <TrendingDown size={14}/>
          <span>
            Customer has <b className="font-mono">{fmtMoney(unallocated_credit_cents)}</b> in
            unallocated credit — will be applied to the next invoice.
          </span>
        </div>
      )}

      {editing && (
        <CreditLimitEditor
          customerId={customerId}
          currentCents={credit_limit_cents}
          onCancel={() => setEditing(false)}
          onSaved={(freshWallet) => {
            setWallet(freshWallet);
            setEditing(false);
          }}
        />
      )}
    </div>
  );
}

// --------------------------------------------------------------------------- //
//  Inline editor for a customer's credit limit.  Owner/admin only.
// --------------------------------------------------------------------------- //
function CreditLimitEditor({ customerId, currentCents, onCancel, onSaved }) {
  const [rand, setRand] = useState(
    currentCents == null ? "" : (currentCents / 100).toFixed(2)
  );
  const [saving, setSaving] = useState(false);

  const _submit = async (payloadCents) => {
    setSaving(true);
    try {
      const { data } = await api.put(
        `/finance/customers/${customerId}/credit-limit`,
        { credit_limit_cents: payloadCents },
      );
      toast.success(
        payloadCents == null
          ? "Credit limit removed"
          : `Credit limit set to R ${(payloadCents / 100).toLocaleString("en-ZA", { minimumFractionDigits: 2 })}`,
      );
      onSaved(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update credit limit");
      setSaving(false);
    }
  };

  const onSave = () => {
    const trimmed = rand.trim();
    if (trimmed === "") {
      toast.error("Enter a value in Rands, or click 'Remove limit'.");
      return;
    }
    const asNumber = Number(trimmed);
    if (!Number.isFinite(asNumber) || asNumber < 0) {
      toast.error("Enter a valid non-negative Rand amount.");
      return;
    }
    // Convert Rands (with 2-decimal precision) → cents (integer).
    const cents = Math.round(asNumber * 100);
    _submit(cents);
  };

  const onRemove = () => {
    if (!window.confirm("Remove the credit limit? The customer will have no facility.")) {
      return;
    }
    _submit(null);
  };

  return (
    <div className="mt-4 border-2 border-blue-400 bg-blue-50/60 rounded p-4"
         data-testid="credit-limit-editor">
      <div className="label-overline text-blue-700 mb-2">Edit credit limit</div>
      <div className="flex items-center gap-2">
        <span className="text-slate-500 font-mono text-sm">R</span>
        <input
          data-testid="credit-limit-input"
          type="number" step="0.01" min="0"
          value={rand}
          onChange={(e) => setRand(e.target.value)}
          disabled={saving}
          placeholder="e.g. 10000.00"
          className="flex-1 h-10 px-3 rounded border border-slate-300 font-mono text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-400"
        />
      </div>
      <div className="mt-3 flex items-center justify-between gap-2">
        <button type="button"
                data-testid="credit-limit-remove-btn"
                onClick={onRemove}
                disabled={saving || currentCents == null}
                className="text-xs text-red-600 hover:text-red-800 underline disabled:opacity-40 disabled:no-underline">
          {currentCents == null ? "(no limit set)" : "Remove limit"}
        </button>
        <div className="flex gap-2">
          <button type="button"
                  data-testid="credit-limit-cancel-btn"
                  onClick={onCancel} disabled={saving}
                  className="text-xs px-3 h-8 rounded border border-slate-300 hover:bg-slate-100 disabled:opacity-40">
            Cancel
          </button>
          <button type="button"
                  data-testid="credit-limit-save-btn"
                  onClick={onSave} disabled={saving}
                  className="text-xs px-4 h-8 rounded bg-blue-700 text-white font-semibold hover:bg-blue-800 disabled:opacity-40">
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Metric({ label, value, tone = "neutral", testId, actionIcon = null }) {
  const tones = {
    neutral: "text-slate-900",
    warn: "text-amber-700",
    danger: "text-red-700",
    good: "text-green-700",
  };
  return (
    <div className="border border-slate-100 rounded p-3" data-testid={testId}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
        {actionIcon}
      </div>
      <div className={"font-mono font-bold text-lg leading-tight " + tones[tone]}>
        {value}
      </div>
    </div>
  );
}

function Bucket({ label, value, testId, danger = false }) {
  return (
    <div className={"border rounded p-2 " +
      (value > 0 && danger ? "border-red-300 bg-red-50" :
       value > 0 ? "border-amber-300 bg-amber-50" :
       "border-slate-200")} data-testid={testId}>
      <div className="text-[9px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className="font-mono text-xs font-bold">{fmtMoney(value)}</div>
    </div>
  );
}
